import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  CardWithHeader, 
} from '../../../../styles/components/common/cards';
import { 
  Input, 
  Textarea, 
  Label, 
  FormGroup, 
  ErrorMessage,
  Select,
  Checkbox
} from '../../../../styles/components/common/forms';
import { 
  Button, 
  SecondaryButton,
  IconButton 
} from '../../../../styles/components/common/buttons';
import { glassDeep, glassMedium } from '../../../../styles/components/common/glassmorphism';

// Styled components for company evaluation form
const EvaluationForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
`;

const FormSection = styled.div`
  ${glassMedium}
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  }
  
  h3 {
    color: rgba(255, 255, 255, 0.9);
    margin-top: 0;
    margin-bottom: 1.25rem;
    font-size: 1.25rem;
    position: relative;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.8));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 600;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -0.5rem;
      left: 0;
      width: 3rem;
      height: 2px;
      background: linear-gradient(90deg, rgba(99, 102, 241, 0.7), transparent);
      border-radius: 1px;
    }
  }
`;

const RatingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const RatingOptions = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const RatingButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, ${({ selected }) => selected ? '0.2' : '0.1'});
  background: ${({ selected }) => selected ? 'rgba(99, 102, 241, 0.4)' : 'rgba(15, 23, 42, 0.5)'};
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  box-shadow: ${({ selected }) => selected ? '0 4px 12px rgba(99, 102, 241, 0.2)' : 'none'};
  
  &:hover {
    background: rgba(99, 102, 241, 0.3);
    transform: translateY(-2px);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.5);
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
  
  button {
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    }
  }
`;

const CharacterCount = styled.small`
  display: block;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  margin-top: 0.25rem;
  text-align: right;
`;

const InternshipInfo = styled.div`
  ${glassDeep}
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
    border-color: rgba(99, 102, 241, 0.3);
  }
  
  h3 {
    color: rgba(255, 255, 255, 0.9);
    margin-top: 0;
    margin-bottom: 1.25rem;
    font-size: 1.25rem;
    position: relative;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.8));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 600;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -0.5rem;
      left: 0;
      width: 3rem;
      height: 2px;
      background: linear-gradient(90deg, rgba(99, 102, 241, 0.7), transparent);
      border-radius: 1px;
    }
  }
  
  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
    
    p {
      margin: 0.5rem 0;
      color: rgba(255, 255, 255, 0.8);
      
      strong {
        color: rgba(255, 255, 255, 0.95);
        margin-right: 0.5rem;
      }
    }
  }
`;

const ToggleSwitch = styled.div`
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  padding: 0.5rem;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  input[type="checkbox"] {
    height: 0;
    width: 0;
    visibility: hidden;
    position: absolute;
  }
  
  label {
    cursor: pointer;
    text-indent: -9999px;
    width: 56px;
    height: 28px;
    background: rgba(15, 23, 42, 0.5);
    display: block;
    border-radius: 100px;
    position: relative;
    margin-right: 1rem;
    transition: all 0.3s ease;
  }
  
  label:after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 22px;
    height: 22px;
    background: #fff;
    border-radius: 90px;
    transition: 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }
  
  input:checked + label {
    background: rgba(99, 102, 241, 0.7);
  }
  
  input:checked + label:after {
    left: calc(100% - 3px);
    transform: translateX(-100%);
  }
  
  label:active:after {
    width: 28px;
  }
  
  span {
    color: white;
    font-size: 0.875rem;
    transition: all 0.3s ease;
  }
  
  input:checked ~ span {
    color: rgba(99, 102, 241, 0.9);
  }
`;

const CompanyEvaluation = ({ internship, onClose, onSave, existingEvaluation, isReadOnly }) => {
  const [evaluation, setEvaluation] = useState({
    workEnvironment: 3,
    mentorship: 3,
    learningOpportunities: 3,
    workLifeBalance: 3,
    compensation: 3,
    overallExperience: 3,
    strengths: '',
    improvements: '',
    additionalComments: '',
    recommended: true
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    if (existingEvaluation) {
      setEvaluation(existingEvaluation);
    }
  }, [existingEvaluation]);
  
  const handleRatingChange = (field, value) => {
    setEvaluation(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };
  
  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setEvaluation(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const handleRecommendationChange = (e) => {
    setEvaluation(prev => ({
      ...prev,
      recommended: e.target.checked
    }));
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!evaluation.strengths.trim() || evaluation.strengths.length < 50) {
      newErrors.strengths = 'Please describe company strengths in at least 50 characters';
    }
    
    if (!evaluation.improvements.trim() || evaluation.improvements.length < 50) {
      newErrors.improvements = 'Please describe areas for improvement in at least 50 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this evaluation? This action cannot be undone.')) {
      onSave(null);
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      onSave({
        ...evaluation,
        submissionDate: new Date().toISOString()
      });
    }, 1000);
  };
  
  const renderRatingOptions = (field, label) => (
    <RatingContainer>
      <Label>{label}</Label>
      <RatingOptions>
        {[1, 2, 3, 4, 5].map(value => (
          <RatingButton
            key={value}
            type="button"
            selected={evaluation[field] === value}
            onClick={() => !isReadOnly && handleRatingChange(field, value)}
            disabled={isReadOnly}
          >
            {value}
          </RatingButton>
        ))}
      </RatingOptions>
    </RatingContainer>
  );
  
  return (
    <CardWithHeader>
      <div className="card-header">
        <h2>{isReadOnly ? 'Company Evaluation' : existingEvaluation ? 'Edit Evaluation' : 'New Company Evaluation'}</h2>
        <IconButton onClick={onClose} size="2rem">
          &times;
        </IconButton>
      </div>
      
      <div className="card-content">
        <InternshipInfo>
          <h3>Internship Details</h3>
          <div className="info-grid">
            <div>
              <p><strong>Company:</strong> {internship.company.name}</p>
              <p><strong>Position:</strong> {internship.position}</p>
            </div>
            <div>
              <p><strong>Duration:</strong> {internship.duration} months</p>
              <p><strong>Dates:</strong> {internship.startDate} to {internship.endDate}</p>
            </div>
          </div>
        </InternshipInfo>
        
        <EvaluationForm onSubmit={handleSubmit}>
          <FormSection>
            <h3>Company Ratings</h3>
            
            {renderRatingOptions('workEnvironment', 'Work Environment')}
            {renderRatingOptions('mentorship', 'Mentorship & Guidance')}
            {renderRatingOptions('learningOpportunities', 'Learning Opportunities')}
            {renderRatingOptions('workLifeBalance', 'Work-Life Balance')}
            {renderRatingOptions('compensation', 'Compensation & Benefits')}
            {renderRatingOptions('overallExperience', 'Overall Experience')}
          </FormSection>
          
          <FormSection>
            <h3>Detailed Feedback</h3>
            
            <FormGroup>
              <Label htmlFor="strengths">Company Strengths</Label>
              <Textarea 
                id="strengths" 
                name="strengths" 
                value={evaluation.strengths} 
                onChange={handleTextChange}
                placeholder="What were the strengths of this company as an internship provider? What did they do well?"
                rows={4}
                disabled={isReadOnly}
              />
              {errors.strengths && <ErrorMessage>{errors.strengths}</ErrorMessage>}
              <CharacterCount>Minimum 50 characters. Current: {evaluation.strengths.length}</CharacterCount>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="improvements">Areas for Improvement</Label>
              <Textarea 
                id="improvements" 
                name="improvements" 
                value={evaluation.improvements} 
                onChange={handleTextChange}
                placeholder="What could the company improve about their internship program?"
                rows={4}
                disabled={isReadOnly}
              />
              {errors.improvements && <ErrorMessage>{errors.improvements}</ErrorMessage>}
              <CharacterCount>Minimum 50 characters. Current: {evaluation.improvements.length}</CharacterCount>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="additionalComments">Additional Comments (Optional)</Label>
              <Textarea 
                id="additionalComments" 
                name="additionalComments" 
                value={evaluation.additionalComments} 
                onChange={handleTextChange}
                placeholder="Any additional comments or feedback you'd like to share?"
                rows={3}
                disabled={isReadOnly}
              />
            </FormGroup>
            
            <FormGroup>
              <ToggleSwitch>
                <input 
                  type="checkbox" 
                  id="recommended" 
                  checked={evaluation.recommended} 
                  onChange={handleRecommendationChange}
                  disabled={isReadOnly}
                />
                <label htmlFor="recommended">Toggle</label>
                <span>I would recommend this company to other students</span>
              </ToggleSwitch>
            </FormGroup>
          </FormSection>
          
          {!isReadOnly && (
            <FormActions>
              <div>
                {existingEvaluation && (
                  <SecondaryButton type="button" onClick={handleDelete}>
                    Delete Evaluation
                  </SecondaryButton>
                )}
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <SecondaryButton type="button" onClick={onClose}>
                  Cancel
                </SecondaryButton>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Evaluation'}
                </Button>
              </div>
            </FormActions>
          )}
        </EvaluationForm>
      </div>
    </CardWithHeader>
  );
};

export default CompanyEvaluation;
